/**
 * Settings Page
 * Admin platform settings and configuration
 */

import React, { useEffect, useState } from 'react';
import { adminService } from '../../services/adminService';

interface Settings {
  // Game Settings
  defaultStartingChips: number;
  defaultAnte: number;
  handTimeoutSeconds: number;
  autoKickInactiveMinutes: number;
  maxTablesPerUser: number;
  enablePrivateTables: boolean;

  // Bot Settings
  enableBotsGlobally: boolean;
  autoFillEmptyTables: boolean;
  maxBotsPerTable: number;
  botSkillLevelMin: number;
  botSkillLevelMax: number;
  botNamesPool: string; // comma-separated

  // Security Settings
  enableJwtExpiration: boolean;
  sessionTimeoutMinutes: number;
  maxLoginAttempts: number;
  adminIpWhitelist: string; // comma-separated IPs
  enable2FAForAdmins: boolean;

  // Notification Settings
  emailOnUserSignup: boolean;
  alertOnSuspiciousActivity: boolean;
  dailyReportEmail: boolean;
  webhookUrl: string;
}

interface SystemStatus {
  serverUptime: number; // seconds
  databaseConnected: boolean;
  redisConnected: boolean;
  webSocketConnections: number;
  memoryUsageMB: number;
  memoryTotalMB: number;
  cpuUsagePercent: number;
}

interface DatabaseStats {
  tables: Array<{
    name: string;
    rows: number;
    sizeMB: number;
  }>;
  totalSizeMB: number;
  totalRows: number;
}

const SettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<Settings>({
    // Game Settings
    defaultStartingChips: 10000,
    defaultAnte: 10,
    handTimeoutSeconds: 30,
    autoKickInactiveMinutes: 10,
    maxTablesPerUser: 5,
    enablePrivateTables: true,

    // Bot Settings
    enableBotsGlobally: true,
    autoFillEmptyTables: false,
    maxBotsPerTable: 4,
    botSkillLevelMin: 3,
    botSkillLevelMax: 8,
    botNamesPool: 'PokerBot, RoboPlayer, AIShark, ChipMaster, BluffKing',

    // Security Settings
    enableJwtExpiration: true,
    sessionTimeoutMinutes: 60,
    maxLoginAttempts: 5,
    adminIpWhitelist: '',
    enable2FAForAdmins: false,

    // Notification Settings
    emailOnUserSignup: true,
    alertOnSuspiciousActivity: true,
    dailyReportEmail: false,
    webhookUrl: '',
  });

  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const [dbStats, setDbStats] = useState<DatabaseStats | null>(null);
  const [expandedSection, setExpandedSection] = useState<string>('game');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [clearLogsOlderThan, setClearLogsOlderThan] = useState(30);

  useEffect(() => {
    loadSettings();
    loadSystemStatus();
    loadDatabaseStats();

    // Refresh system status every 5 seconds
    const interval = setInterval(() => {
      loadSystemStatus();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      // const data = await adminService.getSettings();
      // setSettings(data);
      // Mock - using initial state
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSystemStatus = async () => {
    try {
      // const data = await adminService.getSystemStatus();
      // setSystemStatus(data);
      // Mock data
      setSystemStatus({
        serverUptime: 3600 * 24 * 5 + 3600 * 12 + 45 * 60, // 5 days, 12 hours, 45 minutes
        databaseConnected: true,
        redisConnected: true,
        webSocketConnections: 42,
        memoryUsageMB: 512,
        memoryTotalMB: 2048,
        cpuUsagePercent: 23.5,
      });
    } catch (error) {
      console.error('Failed to load system status:', error);
    }
  };

  const loadDatabaseStats = async () => {
    try {
      // const data = await adminService.getDatabaseStats();
      // setDbStats(data);
      // Mock data
      setDbStats({
        tables: [
          { name: 'users', rows: 1247, sizeMB: 12.5 },
          { name: 'games', rows: 8932, sizeMB: 45.2 },
          { name: 'tables', rows: 156, sizeMB: 3.1 },
          { name: 'bots', rows: 23, sizeMB: 0.8 },
          { name: 'logs', rows: 52341, sizeMB: 128.7 },
        ],
        totalSizeMB: 190.3,
        totalRows: 62699,
      });
    } catch (error) {
      console.error('Failed to load database stats:', error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // await adminService.updateSettings(settings);
      // Mock - simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleBackupDatabase = async () => {
    if (!confirm('Create a database backup? This may take a few minutes.')) return;

    try {
      // await adminService.backupDatabase();
      alert('Database backup initiated! You will receive an email when complete.');
    } catch (error) {
      console.error('Failed to backup database:', error);
      alert('Failed to create backup');
    }
  };

  const handleClearLogs = async () => {
    if (!confirm(`Delete all logs older than ${clearLogsOlderThan} days?`)) return;

    try {
      // await adminService.clearOldLogs(clearLogsOlderThan);
      alert(`Logs older than ${clearLogsOlderThan} days have been deleted.`);
      loadDatabaseStats();
    } catch (error) {
      console.error('Failed to clear logs:', error);
      alert('Failed to clear logs');
    }
  };

  const handleRunMigrations = async () => {
    if (!confirm('Run database migrations? This will update the database schema.')) return;

    try {
      // await adminService.runMigrations();
      alert('Migrations completed successfully!');
    } catch (error) {
      console.error('Failed to run migrations:', error);
      alert('Failed to run migrations');
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? '' : section);
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  const ToggleSwitch: React.FC<{
    enabled: boolean;
    onChange: () => void;
  }> = ({ enabled, onChange }) => (
    <button
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        enabled ? 'bg-green-600' : 'bg-gray-300'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );

  const AccordionSection: React.FC<{
    id: string;
    title: string;
    icon: string;
    children: React.ReactNode;
  }> = ({ id, title, icon, children }) => {
    const isExpanded = expandedSection === id;

    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <button
          onClick={() => toggleSection(id)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <span className="text-2xl">{icon}</span>
            <span className="text-lg font-bold text-gray-800">{title}</span>
          </div>
          <span className="text-2xl text-gray-400">
            {isExpanded ? '▼' : '▶'}
          </span>
        </button>
        {isExpanded && (
          <div className="px-6 py-4 border-t border-gray-200">
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">⚙️ System Settings</h1>
            <p className="text-blue-100 mt-1">Configure platform settings and manage system</p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-3 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-bold disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <span>💾</span>
                <span>Save All Settings</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Success Toast */}
      {showSuccessToast && (
        <div className="fixed top-4 right-4 z-50 bg-green-600 text-white px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3 animate-fade-in">
          <span className="text-2xl">✓</span>
          <div>
            <div className="font-bold">Settings Saved!</div>
            <div className="text-sm text-green-100">All changes have been applied successfully.</div>
          </div>
        </div>
      )}

      {/* Settings Sections */}
      <div className="space-y-4">
        {/* 1. Game Settings */}
        <AccordionSection id="game" title="Game Settings" icon="🎮">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default Starting Chips
                </label>
                <input
                  type="number"
                  min="1000"
                  step="1000"
                  value={settings.defaultStartingChips}
                  onChange={(e) => setSettings({ ...settings, defaultStartingChips: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hand Timeout (seconds)
                </label>
                <input
                  type="number"
                  min="10"
                  max="120"
                  value={settings.handTimeoutSeconds}
                  onChange={(e) => setSettings({ ...settings, handTimeoutSeconds: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default Ante
                </label>
                <input
                  type="number"
                  min="1"
                  value={settings.defaultAnte}
                  onChange={(e) => setSettings({ ...settings, defaultAnte: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Auto-kick Inactive (minutes)
                </label>
                <input
                  type="number"
                  min="5"
                  max="60"
                  value={settings.autoKickInactiveMinutes}
                  onChange={(e) => setSettings({ ...settings, autoKickInactiveMinutes: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Tables Per User
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={settings.maxTablesPerUser}
                  onChange={(e) => setSettings({ ...settings, maxTablesPerUser: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-800">Enable Private Tables</div>
                <div className="text-sm text-gray-500">Allow users to create password-protected tables</div>
              </div>
              <ToggleSwitch
                enabled={settings.enablePrivateTables}
                onChange={() => setSettings({ ...settings, enablePrivateTables: !settings.enablePrivateTables })}
              />
            </div>
          </div>
        </AccordionSection>

        {/* 2. Bot Settings */}
        <AccordionSection id="bot" title="Bot Settings" icon="🤖">
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-800">Enable Bots Globally</div>
                <div className="text-sm text-gray-500">Allow bots to join games across the platform</div>
              </div>
              <ToggleSwitch
                enabled={settings.enableBotsGlobally}
                onChange={() => setSettings({ ...settings, enableBotsGlobally: !settings.enableBotsGlobally })}
              />
            </div>

            <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-800">Auto-fill Empty Tables</div>
                <div className="text-sm text-gray-500">Automatically add bots to tables with few players</div>
              </div>
              <ToggleSwitch
                enabled={settings.autoFillEmptyTables}
                onChange={() => setSettings({ ...settings, autoFillEmptyTables: !settings.autoFillEmptyTables })}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Bots Per Table
                </label>
                <input
                  type="number"
                  min="0"
                  max="8"
                  value={settings.maxBotsPerTable}
                  onChange={(e) => setSettings({ ...settings, maxBotsPerTable: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bot Skill Level Min
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={settings.botSkillLevelMin}
                  onChange={(e) => setSettings({ ...settings, botSkillLevelMin: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bot Skill Level Max
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={settings.botSkillLevelMax}
                  onChange={(e) => setSettings({ ...settings, botSkillLevelMax: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bot Names Pool (comma-separated)
              </label>
              <textarea
                rows={3}
                value={settings.botNamesPool}
                onChange={(e) => setSettings({ ...settings, botNamesPool: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Bot1, Bot2, Bot3..."
              />
              <div className="text-xs text-gray-500 mt-1">
                Random names will be selected from this pool when creating bots
              </div>
            </div>
          </div>
        </AccordionSection>

        {/* 3. Security Settings */}
        <AccordionSection id="security" title="Security Settings" icon="🔒">
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-800">Enable JWT Expiration</div>
                <div className="text-sm text-gray-500">Require users to re-login after session expires</div>
              </div>
              <ToggleSwitch
                enabled={settings.enableJwtExpiration}
                onChange={() => setSettings({ ...settings, enableJwtExpiration: !settings.enableJwtExpiration })}
              />
            </div>

            <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-800">Enable 2FA for Admins</div>
                <div className="text-sm text-gray-500">Require two-factor authentication for admin accounts</div>
              </div>
              <ToggleSwitch
                enabled={settings.enable2FAForAdmins}
                onChange={() => setSettings({ ...settings, enable2FAForAdmins: !settings.enable2FAForAdmins })}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Session Timeout (minutes)
                </label>
                <input
                  type="number"
                  min="15"
                  max="1440"
                  value={settings.sessionTimeoutMinutes}
                  onChange={(e) => setSettings({ ...settings, sessionTimeoutMinutes: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Login Attempts
                </label>
                <input
                  type="number"
                  min="3"
                  max="10"
                  value={settings.maxLoginAttempts}
                  onChange={(e) => setSettings({ ...settings, maxLoginAttempts: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admin IP Whitelist (comma-separated IPs)
              </label>
              <textarea
                rows={2}
                value={settings.adminIpWhitelist}
                onChange={(e) => setSettings({ ...settings, adminIpWhitelist: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="192.168.1.1, 10.0.0.1, ..."
              />
              <div className="text-xs text-gray-500 mt-1">
                Leave empty to allow all IPs. Only listed IPs can access admin panel.
              </div>
            </div>
          </div>
        </AccordionSection>

        {/* 4. Notification Settings */}
        <AccordionSection id="notifications" title="Notification Settings" icon="🔔">
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-800">Email on User Signup</div>
                <div className="text-sm text-gray-500">Send email notification when new users register</div>
              </div>
              <ToggleSwitch
                enabled={settings.emailOnUserSignup}
                onChange={() => setSettings({ ...settings, emailOnUserSignup: !settings.emailOnUserSignup })}
              />
            </div>

            <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-800">Alert on Suspicious Activity</div>
                <div className="text-sm text-gray-500">Send alerts when suspicious behavior is detected</div>
              </div>
              <ToggleSwitch
                enabled={settings.alertOnSuspiciousActivity}
                onChange={() => setSettings({ ...settings, alertOnSuspiciousActivity: !settings.alertOnSuspiciousActivity })}
              />
            </div>

            <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-800">Daily Report Email</div>
                <div className="text-sm text-gray-500">Receive daily summary reports via email</div>
              </div>
              <ToggleSwitch
                enabled={settings.dailyReportEmail}
                onChange={() => setSettings({ ...settings, dailyReportEmail: !settings.dailyReportEmail })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Webhook URL for Events
              </label>
              <input
                type="url"
                value={settings.webhookUrl}
                onChange={(e) => setSettings({ ...settings, webhookUrl: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://your-webhook-endpoint.com/events"
              />
              <div className="text-xs text-gray-500 mt-1">
                POST requests will be sent to this URL for important platform events
              </div>
            </div>
          </div>
        </AccordionSection>

        {/* 5. Database Management */}
        <AccordionSection id="database" title="Database Management" icon="💾">
          <div className="space-y-6">
            {/* Database Stats */}
            {dbStats && (
              <div>
                <h4 className="font-bold text-gray-800 mb-3">Database Statistics</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <div className="text-sm text-blue-600 font-semibold">Total Size</div>
                    <div className="text-2xl font-bold text-blue-900 mt-1">
                      {dbStats.totalSizeMB.toFixed(1)} MB
                    </div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <div className="text-sm text-purple-600 font-semibold">Total Rows</div>
                    <div className="text-2xl font-bold text-purple-900 mt-1">
                      {dbStats.totalRows.toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Table</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Rows</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Size (MB)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {dbStats.tables.map(table => (
                        <tr key={table.name} className="hover:bg-gray-50">
                          <td className="px-4 py-2 font-medium text-gray-900">{table.name}</td>
                          <td className="px-4 py-2 text-right text-gray-600">{table.rows.toLocaleString()}</td>
                          <td className="px-4 py-2 text-right text-gray-600">{table.sizeMB.toFixed(1)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Database Actions */}
            <div className="space-y-3">
              <h4 className="font-bold text-gray-800">Database Actions</h4>

              <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
                <div>
                  <div className="font-medium text-gray-800">Run Migrations</div>
                  <div className="text-sm text-gray-500">Update database schema to latest version</div>
                </div>
                <button
                  onClick={handleRunMigrations}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Run Now
                </button>
              </div>

              <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
                <div>
                  <div className="font-medium text-gray-800">Backup Database</div>
                  <div className="text-sm text-gray-500">Create a full database backup</div>
                </div>
                <button
                  onClick={handleBackupDatabase}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Backup Now
                </button>
              </div>

              <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="font-medium text-gray-800">Clear Old Logs</div>
                  <div className="text-sm text-gray-500">Delete logs older than specified days</div>
                  <div className="mt-2 flex items-center space-x-2">
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={clearLogsOlderThan}
                      onChange={(e) => setClearLogsOlderThan(parseInt(e.target.value))}
                      className="w-24 px-3 py-1 border border-gray-300 rounded text-sm"
                    />
                    <span className="text-sm text-gray-600">days</span>
                  </div>
                </div>
                <button
                  onClick={handleClearLogs}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Clear Logs
                </button>
              </div>
            </div>
          </div>
        </AccordionSection>

        {/* 6. System Info */}
        <AccordionSection id="system" title="System Information" icon="📊">
          <div className="space-y-4">
            {systemStatus ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                    <div className="text-sm text-blue-600 font-semibold mb-1">Server Uptime</div>
                    <div className="text-2xl font-bold text-blue-900">
                      {formatUptime(systemStatus.serverUptime)}
                    </div>
                  </div>

                  <div className={`rounded-lg p-4 border ${
                    systemStatus.databaseConnected
                      ? 'bg-gradient-to-br from-green-50 to-green-100 border-green-200'
                      : 'bg-gradient-to-br from-red-50 to-red-100 border-red-200'
                  }`}>
                    <div className={`text-sm font-semibold mb-1 ${
                      systemStatus.databaseConnected ? 'text-green-600' : 'text-red-600'
                    }`}>
                      Database Status
                    </div>
                    <div className={`text-2xl font-bold ${
                      systemStatus.databaseConnected ? 'text-green-900' : 'text-red-900'
                    }`}>
                      {systemStatus.databaseConnected ? '✓ Connected' : '✗ Disconnected'}
                    </div>
                  </div>

                  <div className={`rounded-lg p-4 border ${
                    systemStatus.redisConnected
                      ? 'bg-gradient-to-br from-green-50 to-green-100 border-green-200'
                      : 'bg-gradient-to-br from-red-50 to-red-100 border-red-200'
                  }`}>
                    <div className={`text-sm font-semibold mb-1 ${
                      systemStatus.redisConnected ? 'text-green-600' : 'text-red-600'
                    }`}>
                      Redis Status
                    </div>
                    <div className={`text-2xl font-bold ${
                      systemStatus.redisConnected ? 'text-green-900' : 'text-red-900'
                    }`}>
                      {systemStatus.redisConnected ? '✓ Connected' : '✗ Disconnected'}
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                    <div className="text-sm text-purple-600 font-semibold mb-1">WebSocket Connections</div>
                    <div className="text-2xl font-bold text-purple-900">
                      {systemStatus.webSocketConnections} live
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
                    <div className="text-sm text-orange-600 font-semibold mb-1">Memory Usage</div>
                    <div className="text-2xl font-bold text-orange-900">
                      {systemStatus.memoryUsageMB} MB
                    </div>
                    <div className="text-xs text-orange-600 mt-1">
                      of {systemStatus.memoryTotalMB} MB ({((systemStatus.memoryUsageMB / systemStatus.memoryTotalMB) * 100).toFixed(1)}%)
                    </div>
                    <div className="w-full bg-orange-200 rounded-full h-2 mt-2">
                      <div
                        className="bg-orange-600 h-2 rounded-full"
                        style={{ width: `${(systemStatus.memoryUsageMB / systemStatus.memoryTotalMB) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg p-4 border border-pink-200">
                    <div className="text-sm text-pink-600 font-semibold mb-1">CPU Usage</div>
                    <div className="text-2xl font-bold text-pink-900">
                      {systemStatus.cpuUsagePercent.toFixed(1)}%
                    </div>
                    <div className="w-full bg-pink-200 rounded-full h-2 mt-3">
                      <div
                        className="bg-pink-600 h-2 rounded-full"
                        style={{ width: `${systemStatus.cpuUsagePercent}%` }}
                      ></div>
                    </div>
                  </div>
                </div>

                <div className="text-xs text-gray-500 text-center">
                  Auto-refreshing every 5 seconds
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">Loading system information...</div>
            )}
          </div>
        </AccordionSection>
      </div>

      {/* Bottom Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center space-x-2"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Saving Changes...</span>
            </>
          ) : (
            <>
              <span>💾</span>
              <span>Save All Settings</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default SettingsPage;
